import React, { useState, useEffect } from 'react';
import "../../css/Records.css";
import { Search, Download, Filter, Trash2, Eye, Calendar, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { format } from 'date-fns'; 

function Records() {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    paymentMethod: '',
    minAmount: '',
    maxAmount: '',
    feeType: ''
  });

  // Fee types for filter dropdown
  const feeTypes = [
    'Admission Fee',
    'Tuition Fee',
    'Hostel Fee',
    'Food Fee',
    'Library Fee'
  ];

  // Payment methods for filter dropdown
  const paymentMethods = ['UPI', 'Bank Transfer', 'Cash'];

  useEffect(() => {
    // Fetch payments for today's date by default
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setIsLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      // Format current date for API call
      const today = format(new Date(), 'yyyy-MM-dd');

      const response = await fetch(`https://schoolpro-api.uniums.com/api/receipt/payments?startDate=${today}&endDate=${today}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch payment records");
      }

      const data = await response.json();
      setPayments(data);
      setFilteredPayments(data);
    } catch (error) {
      console.error("Error fetching payment records:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = async () => {
    setIsLoading(true);

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      // Build query parameters based on filter values
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.minAmount) params.append('minAmount', filters.minAmount);
      if (filters.maxAmount) params.append('maxAmount', filters.maxAmount);
      if (filters.feeType) params.append('feeType', filters.feeType);

      const response = await fetch(`https://schoolpro-api.uniums.com/api/receipt/payments?${params}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch filtered payment records");
      }

      const data = await response.json();
      setPayments(data);
      setFilteredPayments(data);
      setShowFilters(false);
      setCurrentPage(1);
      toast.success("Filters applied successfully!");
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      paymentMethod: '',
      minAmount: '',
      maxAmount: '',
      feeType: ''
    });
    fetchPayments();
    setShowFilters(false);
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query) {
      setFilteredPayments(payments);
      return;
    }

    const filtered = payments.filter(payment =>
      payment.student?.firstName?.toLowerCase().includes(query) ||
      payment.student?.lastName?.toLowerCase().includes(query) ||
      payment.student?.rollNumber?.toLowerCase().includes(query) ||
      payment.receiptId.toLowerCase().includes(query)
    );

    setFilteredPayments(filtered);
    setCurrentPage(1);
  };


  const downloadReceipt = async (receipt) => {
    try {
      if (!receipt.pdfUrl) {
        throw new Error("Receipt URL not found");
      }
  
      const loadingToast = toast.loading("Downloading receipt...");
  
      // Ensure the URL is properly formatted
      const receiptUrl = receipt.pdfUrl;
  
      if (!receiptUrl.startsWith('http')) {
        throw new Error("Invalid PDF URL");
      }
  
      const link = document.createElement('a');
      link.href = receiptUrl;
      link.target = "_blank";
      link.download = `Receipt_${receipt.receiptId}.pdf`;
  
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  
      toast.dismiss(loadingToast);
      toast.success("Receipt opened successfully");
    } catch (error) {
      console.error("Error accessing receipt:", error);
      toast.error(`Failed to access receipt: ${error.message}`);
    }
  };
  
  

  const viewReceiptDetails = (receipt) => {
    setViewingReceipt(receipt);
  };

  const closeReceiptView = () => {
    setViewingReceipt(null);
  };

  // Calculate pagination indices
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date string to more readable format
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMM yyyy');
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  return (
    <div className="Records-container">
      <div className="Records-header">
        <h1>Payment Records</h1>
        <p>View and manage all student fee payment records</p>
      </div>

      <div className="Records-controls">
        <div className="Records-search">
          <div className="Records-search-bar">
            <input
              type="text"
              placeholder="Search by student name, roll number or receipt ID..."
              value={searchQuery}
              onChange={handleSearch}
            />
            <Search size={18} className="Records-search-icon" />
          </div>

          <button
            className="Records-filter-button"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="Records-filters-panel">
            <div className="Records-filters-form">
              <div className="Records-filter-row">
                <div className="Records-filter-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="Records-filter-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="Records-filter-group">
                  <label>Payment Method</label>
                  <select
                    name="paymentMethod"
                    value={filters.paymentMethod}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Methods</option>
                    {paymentMethods.map(method => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="Records-filter-row">
                <div className="Records-filter-group">
                  <label>Min Amount (₹)</label>
                  <input
                    type="number"
                    name="minAmount"
                    value={filters.minAmount}
                    onChange={handleFilterChange}
                    placeholder="0"
                  />
                </div>

                <div className="Records-filter-group">
                  <label>Max Amount (₹)</label>
                  <input
                    type="number"
                    name="maxAmount"
                    value={filters.maxAmount}
                    onChange={handleFilterChange}
                    placeholder="100000"
                  />
                </div>

                <div className="Records-filter-group">
                  <label>Fee Type</label>
                  <select
                    name="feeType"
                    value={filters.feeType}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Fee Types</option>
                    {feeTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="Records-filter-actions">
                <button className="Records-apply-button" onClick={applyFilters}>
                  Apply Filters
                </button>
                <button className="Records-clear-button" onClick={clearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <>
          <Loader />
          <div className="Records-loading">
            <div className="Records-spinner"></div>
            <p>Loading payment records...</p>
          </div>
        </>

      ) : filteredPayments.length > 0 ? (
        <>
          <div className="Records-table-container">
            <table className="Records-table">
              <thead>
                <tr>
                  <th>Receipt ID</th>
                  <th>Student Name</th>
                  <th>Roll Number</th>
                  <th>Payment Date</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((payment) => (
                  <tr key={payment._id}>
                    <td>{payment.receiptId}</td>
                    <td>{payment.student?.firstName} {payment.student?.lastName}</td>
                    <td>{payment.student?.rollNumber}</td>
                    <td>{formatDate(payment.paymentDate)}</td>
                    <td className="Records-amount">{formatCurrency(payment.amount)}</td>
                    <td>
                      <span className={`Records-payment-method Records-method-${payment.paymentMethod.toLowerCase().replace(/\s+/g, '-')}`}>
                        {payment.paymentMethod}
                      </span>
                    </td>
                    <td className="Records-actions">
                      <button
                        className="Records-view-button"
                        onClick={() => viewReceiptDetails(payment)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="Records-download-button"
                        onClick={() => downloadReceipt(payment)}
                        title="Download Receipt"
                      >
                        <Download size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="Records-pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="Records-pagination-button"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="Records-page-info">
              Page {currentPage} of {totalPages} ({filteredPayments.length} records)
            </div>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="Records-pagination-button"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </>
      ) : (
        <div className="Records-no-data">
          <AlertCircle size={48} />
          <h3>No payment records found</h3>
          <p>No records match your current search or filter criteria. Try adjusting your filters or search for different terms.</p>
          {filters.startDate !== format(new Date(), 'yyyy-MM-dd') ||
            filters.endDate !== format(new Date(), 'yyyy-MM-dd') ||
            filters.paymentMethod || filters.minAmount || filters.maxAmount || filters.feeType ? (
            <button className="Records-reset-button" onClick={clearFilters}>
              Reset to Today's Records
            </button>
          ) : null}
        </div>
      )}

      {/* Receipt Detail Modal */}
      {viewingReceipt && (
        <div className="Records-modal-overlay">
          <div className="Records-receipt-modal">
            <div className="Records-modal-header">
              <h2>Receipt Details</h2>
              <button className="Records-modal-close" onClick={closeReceiptView}>×</button>
            </div>

            <div className="Records-modal-content">
              <div className="Records-receipt-header">
                <div className="Records-receipt-title">
                  <Calendar size={32} className="Records-pdf-icon" />
                  <div>
                    <h3>Receipt #{viewingReceipt.receiptId}</h3>
                    <p>Generated on {formatDate(viewingReceipt.createdAt)}</p>
                  </div>
                </div>

                <div className="Records-receipt-actions">
                  <button className="Records-receipt-download" onClick={() => downloadReceipt(viewingReceipt)}>
                    <Download size={18} />
                    Download PDF
                  </button>
                </div>
              </div>

              <div className="Records-receipt-details">
                <div className="Records-detail-section">
                  <h4>Student Information</h4>
                  <div className="Records-detail-grid">
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Name</span>
                      <span className="Records-detail-value">{viewingReceipt.student?.firstName} {viewingReceipt.student?.lastName}</span>
                    </div>
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Roll Number</span>
                      <span className="Records-detail-value">{viewingReceipt.student?.rollNumber}</span>
                    </div>
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Grade</span>
                      <span className="Records-detail-value">{viewingReceipt.student?.grade}-{viewingReceipt.student?.section}</span>
                    </div>
                  </div>
                </div>

                <div className="Records-detail-section">
                  <h4>Payment Information</h4>
                  <div className="Records-detail-grid">
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Payment Date</span>
                      <span className="Records-detail-value">{formatDate(viewingReceipt.paymentDate)}</span>
                    </div>
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Payment Method</span>
                      <span className="Records-detail-value">
                        <span className={`Records-payment-method Records-method-${viewingReceipt.paymentMethod.toLowerCase().replace(/\s+/g, '-')}`}>
                          {viewingReceipt.paymentMethod}
                        </span>
                      </span>
                    </div>
                    <div className="Records-detail-item">
                      <span className="Records-detail-label">Total Amount</span>
                      <span className="Records-detail-value Records-amount-large">{formatCurrency(viewingReceipt.amount)}</span>
                    </div>
                  </div>
                </div>

                <div className="Records-detail-section">
                  <h4>Fee Breakdown</h4>
                  <table className="Records-fee-table">
                    <thead>
                      <tr>
                        <th>Fee Type</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingReceipt.feeTypes.map((fee, index) => (
                        <tr key={index}>
                          <td>{fee.type}</td>
                          <td>{formatCurrency(fee.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td><strong>{formatCurrency(viewingReceipt.amount)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="Records-receipt-preview">
                  <h4>Receipt Preview</h4>
                  <div className="Records-pdf-preview">
                    <div className="Records-pdf-placeholder">
                      <Calendar size={64} />
                      <p>PDF Receipt</p>
                      <button className="Records-open-pdf" onClick={() => window.open(viewingReceipt.cloudinaryUrl, '_blank')}>
                        View Full Receipt
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

export default Records;